import { AlertTriangle, Calendar, Building2, MessageSquare, Bot } from 'lucide-react'
import { Card, CardHeader, CardTitle, CardContent, Button, Skeleton } from '@/components/ui'
import { useMyTasks } from '@/hooks'
import { useUiStore } from '@/stores/uiStore'
import { useStreamChat } from '@/hooks'
import { cn, daysUntil, formatDate } from '@/lib/utils'
import type { Task } from '@/api'

/**
 * Urgency score for sorting: lower = more urgent.
 */
function urgencyScore(task: Task): number {
  if (!task.due_date) return 100

  const days = daysUntil(task.due_date)
  if (days < 0) return 0 // overdue — top priority
  if (days === 0) return 1 // due today
  if (task.priority === 'urgent') return 2
  if (task.priority === 'high') return 3
  if (task.priority === 'medium') return 10 + days
  return 20 + days
}

function getPriorityBorderColor(task: Task): string {
  if (!task.due_date) {
    if (task.priority === 'urgent') return 'border-l-red-500'
    if (task.priority === 'high') return 'border-l-blue-500'
    return 'border-l-muted-foreground'
  }

  const days = daysUntil(task.due_date)
  if (days < 0) return 'border-l-red-500'
  if (days === 0) return 'border-l-amber-500'
  if (task.priority === 'urgent' || task.priority === 'high')
    return 'border-l-blue-500'
  return 'border-l-muted-foreground'
}

function getDeadlineLabel(task: Task): string | null {
  if (!task.due_date) return null
  const days = daysUntil(task.due_date)
  if (days < 0) return `Overdue by ${Math.abs(days)}d`
  if (days === 0) return 'Due today'
  if (days === 1) return 'Due tomorrow'
  return formatDate(task.due_date)
}

export function PriorityTaskList() {
  const { data, isLoading } = useMyTasks()
  const { setChatOpen } = useUiStore()
  const { send } = useStreamChat()

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" />
            Priority Tasks
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-[60px] rounded-lg" />
          ))}
        </CardContent>
      </Card>
    )
  }

  // Filter to non-completed, sort by urgency, take top 5
  const tasks = (data?.items ?? [])
    .filter((t) => t.status !== 'completed')
    .sort((a, b) => urgencyScore(a) - urgencyScore(b))
    .slice(0, 5)

  const handleAskCopilot = (task: Task) => {
    setChatOpen(true)
    send(`What should I know about "${task.title}"?`)
  }

  if (tasks.length === 0) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" />
            Priority Tasks
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground text-center py-6">
            No active tasks — all caught up
          </p>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <AlertTriangle className="h-4 w-4" />
          Priority Tasks
          <span className="ml-auto text-[10px] font-normal px-2 py-0.5 rounded-full bg-gradient-to-r from-primary/10 to-[hsl(280,100%,70%)]/10 border border-primary/20 text-primary flex items-center gap-1">
            <Bot className="h-3 w-3" />
            Agent priority
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {tasks.map((task) => {
          const days = task.due_date ? daysUntil(task.due_date) : null
          const isOverdue = days !== null && days < 0
          const deadlineLabel = getDeadlineLabel(task)

          return (
            <div
              key={task.id}
              className={cn(
                'flex items-center justify-between p-3 rounded-lg border-l-4 bg-muted/20',
                'hover:bg-muted/40 transition-colors',
                getPriorityBorderColor(task),
              )}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium truncate">{task.title}</p>
                  {isOverdue && (
                    <span className="flex-shrink-0 text-[10px] font-semibold font-mono px-1.5 py-0.5 rounded bg-red-500/10 text-red-600 dark:text-red-400">
                      OVERDUE
                    </span>
                  )}
                  {days === 0 && (
                    <span className="flex-shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-600 dark:text-amber-400">
                      TODAY
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                  {task.client_name && (
                    <span className="flex items-center gap-1">
                      <Building2 className="h-3 w-3" />
                      {task.client_name}
                    </span>
                  )}
                  {deadlineLabel && (
                    <span
                      className={cn(
                        'flex items-center gap-1',
                        isOverdue && 'text-red-600 dark:text-red-400',
                        days === 0 && 'text-amber-600 dark:text-amber-400',
                      )}
                    >
                      <Calendar className="h-3 w-3" />
                      {deadlineLabel}
                    </span>
                  )}
                </div>
              </div>

              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 flex-shrink-0 ml-2"
                title="Ask Copilot about this task"
                onClick={() => handleAskCopilot(task)}
              >
                <MessageSquare className="h-3.5 w-3.5" />
              </Button>
            </div>
          )
        })}
      </CardContent>
    </Card>
  )
}
